import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Header } from '@/components/Header';
import { Button } from '@/components/ui/button';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import { Users, CheckCircle, XCircle, Loader2 } from 'lucide-react';
import { apiFetch } from '@/hooks/useFetch';

const JoinSubscriptionPage = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const { user, isAuthenticated, isLoading: authLoading } = useAuth();
  
  const [linkInfo, setLinkInfo] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isJoining, setIsJoining] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    const fetchLinkInfo = async () => {
      try {
        const { data, error } = await apiFetch(`/api/subscription/share-link/${token}`);
        if (error) {
          setError(error || 'Invalid or expired invite link');
        } else {
          setLinkInfo(data);
        }
      } catch (err) {
        setError('Failed to load invite information');
      } finally {
        setIsLoading(false);
      }
    };

    if (token) {
      fetchLinkInfo();
    }
  }, [token]);

  const handleJoin = async () => {
    if (!isAuthenticated) {
      // Redirect to login with return URL
      navigate(`/login?redirect=/join/${token}`);
      return;
    }

    setIsJoining(true);
    try {
      const { data, error } = await apiFetch(`/api/subscription/join-via-link/${token}`, {
        method: 'POST'
      });

      if (error) {
        toast.error(error || 'Failed to join subscription');
      } else {
        setSuccess(true);
        toast.success(`You've joined ${data.owner_name}'s VIP group!`);
        setTimeout(() => navigate('/'), 2000);
      }
    } catch (err) {
      toast.error('Something went wrong');
    } finally {
      setIsJoining(false);
    }
  };

  if (isLoading || authLoading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="container py-12 flex justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="container py-12">
          <div className="max-w-md mx-auto">
            <Card>
              <CardHeader className="text-center">
                <div className="mx-auto w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mb-4">
                  <XCircle className="h-6 w-6 text-red-600" />
                </div>
                <CardTitle className="text-2xl">Invalid Invite</CardTitle>
                <CardDescription>{error}</CardDescription>
              </CardHeader>
              <CardContent className="text-center">
                <Button onClick={() => navigate('/')}>Go Home</Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="container py-12">
          <div className="max-w-md mx-auto">
            <Card>
              <CardHeader className="text-center">
                <div className="mx-auto w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mb-4">
                  <CheckCircle className="h-6 w-6 text-green-600" />
                </div>
                <CardTitle className="text-2xl">Welcome!</CardTitle>
                <CardDescription>You've successfully joined the ClearCommunicationApp VIP group!</CardDescription>
              </CardHeader>
              <CardContent className="text-center">
                <p className="text-muted-foreground mb-4">Redirecting you to the app...</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      <div className="container py-12">
        <div className="max-w-md mx-auto">
          <Card>
            <CardHeader className="text-center">
              <div className="mx-auto w-12 h-12 bg-[#00E5CC]/10 rounded-full flex items-center justify-center mb-4">
                <Users className="h-6 w-6 text-[#00E5CC]" />
              </div>
              <CardTitle className="text-2xl">You're Invited!</CardTitle>
              <CardDescription>
                <strong>{linkInfo?.owner_name}</strong> has invited you to join their ClearCommunicationApp VIP group.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="bg-slate-50 p-4 rounded-lg">
                <h3 className="font-medium mb-2">What you'll get:</h3>
                <ul className="text-sm text-muted-foreground space-y-1">
                  <li>• Full access to 3,400+ phrases</li>
                  <li>• Understand implied meanings</li>
                  <li>• AI-powered communication decoder</li>
                  <li>• Medical terminology library</li>
                </ul>
              </div>
              
              {isAuthenticated ? (
                <Button 
                  onClick={handleJoin} 
                  className="w-full" 
                  disabled={isJoining}
                >
                  {isJoining ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Joining...
                    </>
                  ) : (
                    'Accept Invitation'
                  )}
                </Button>
              ) : (
                <div className="space-y-2">
                  <Button onClick={handleJoin} className="w-full">
                    Sign In to Accept
                  </Button>
                  <p className="text-xs text-center text-muted-foreground">
                    Don't have an account? You'll be able to create one.
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default JoinSubscriptionPage;
